import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { impactplanAPI } from '../services/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../stores/authStore';

interface ImpactPlanForm {
  serviceLine: string;
  customerName?: string;
  customerId?: string;
  actualCustomerName?: string;
  nickname?: string;
  kitNumber?: string;
  isActive: boolean;
  currentUsage: number;
  usagePercentage: number;
  packageLimit: number;
  dataTopup: number;
  notified75: boolean;
  notified100: boolean;
  status: string;
  monitorStatus: string;
  activationDate?: string;
  endDate?: string;
  lastChecked: string;
}

const ImpactPlan: React.FC = () => {
  const [formsSearch, setFormsSearch] = useState('');
  const [filterOption, setFilterOption] = useState<string>('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingForm, setEditingForm] = useState<ImpactPlanForm | null>(null);
  const [customerFormData, setCustomerFormData] = useState({
    customerName: ''
  });
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const debounce = useRef<NodeJS.Timeout>();
  const queryClient = useQueryClient();

  const getUsageColor = (usage: number, limit: number) => {
    const percentage = (usage / limit) * 100;
    if (percentage >= 100) return 'text-red-600 font-bold';
    if (percentage >= 75) return 'text-yellow-600 font-semibold';
    if (percentage >= 50) return 'text-orange-600';
    return 'text-green-600';
  };


  // Fetch Monitor status using React Query (like Dashboard)
  const { data: monitorData, isLoading: monitorLoading, refetch: refetchMonitor } = useQuery(
    'impactplan-monitor-status',
    impactplanAPI.getStatus,
    {
      refetchInterval: 30000, // Refresh every 30 seconds
      onError: (error: any) => {
        console.warn('Impact Plan monitor status fetch failed:', error.message);
      },
    }
  );

  // Ensure we're reading the running property correctly
  const monitorRunning = Boolean(monitorData?.running);

  // React Query for Impact Plan forms data - fetch all forms (like Service Forms)
  const { data: formsData, isLoading: formsLoading } = useQuery(
    ['impactplan-forms'],
    () => impactplanAPI.getForms(),
    {
      onError: (error: any) => {
        console.error('Error fetching impact plan forms:', error);
        toast.error('Failed to load impact plan forms');
      },
    }
  );

  // Client-side filtering exactly like Service Forms
  const filteredForms = formsData ? formsData.filter((form: ImpactPlanForm) => {
    const haystack = `${form.customerName || ''} ${form.serviceLine} ${form.customerId || ''} ${form.kitNumber || ''}`.toLowerCase();
    const matches = haystack.includes(formsSearch.toLowerCase());
    return matches;
  }) : [];

  const applyFilter = (forms: ImpactPlanForm[]): ImpactPlanForm[] => {
    switch (filterOption) {
      case 'active':
        return forms.filter(f => f.isActive);
      case 'inactive':
        return forms.filter(f => !f.isActive);
      case '75':
        return forms.filter(f => (f.usagePercentage ?? (f.currentUsage / (f.packageLimit + (f.dataTopup || 0)))*100) >= 75);
      default:
        return forms;
    }
  };

  const displayedForms = applyFilter(filteredForms);

  const getMonitorColor = (status?: string) => {
    if (status === 'Monitored') return 'bg-green-100 text-green-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Mutation for saving customer information
  const saveCustomerMutation = useMutation(
    ({ serviceLine, customerName }: { serviceLine: string; customerName: string }) =>
      impactplanAPI.saveCustomer(serviceLine, customerName),
    {
      onSuccess: () => {
        toast.success('Customer information saved successfully! ✨');
        queryClient.invalidateQueries(['impactplan-forms']);
        setShowEditModal(false);
        setEditingForm(null);
        setCustomerFormData({ customerName: '' });
      },
      onError: (error: any) => {
        const message = error.response?.data?.message || 'Failed to save customer information';
        toast.error(message);
      }
    }
  );

  const handleEdit = (form: ImpactPlanForm) => {
    setEditingForm(form);
    // Only use actualCustomerName (from impactplan_customer table), not the fallback nickname
    setCustomerFormData({
      customerName: form.actualCustomerName || ''
    });
    setShowEditModal(true);
  };

  const handleSaveCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingForm) return;

    if (!customerFormData.customerName.trim()) {
      toast.error('Customer Name is required');
      return;
    }

    saveCustomerMutation.mutate({
      serviceLine: editingForm.serviceLine,
      customerName: customerFormData.customerName.trim()
    });
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setEditingForm(null);
    setCustomerFormData({ customerName: '' });
  };

  const handleStartMonitor = async () => {
    try {
      await impactplanAPI.startMonitor();
      toast.success('Impact Plan monitor started successfully 🚀');
      refetchMonitor();
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Failed to start monitor';
      toast.error(msg);
    }
  };

  const handleStopMonitor = async () => {
    try {
      await impactplanAPI.stopMonitor();
      toast.success('Impact Plan monitor stopped successfully ✋');
      refetchMonitor();
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Failed to stop monitor';
      toast.error(msg);
    }
  };

  const handleExport = async () => {
    try {
      toast.loading('Preparing export... This may take a moment for large datasets.', { duration: 3000 });
      
      const exportFilters = {
        search: formsSearch,
        filterOption: filterOption
      };
      
      const blob = await impactplanAPI.exportForms(exportFilters);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `impact_plan_forms_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Impact Plan forms exported successfully 📄');
    } catch (error: any) {
      console.error('Export failed:', error);
      if (error.response?.status === 422) {
        toast.error('No forms found to export');
      } else if (error.response?.status === 429) {
        toast.error('Rate limit exceeded. Please wait a moment and try again.');
      } else {
        toast.error('Failed to export forms. Please try again.');
      }
    }
  };

  if (formsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading impact plan forms...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 p-8 text-white shadow-2xl">
        <div className="absolute inset-0 opacity-30 [background:radial-gradient(circle_at_15%_20%,rgba(56,189,248,0.35),transparent_45%),radial-gradient(circle_at_85%_25%,rgba(167,139,250,0.28),transparent_55%)]" />
        <div className="relative flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 backdrop-blur">
              <span
                key={`status-${monitorRunning}`}
                className={`h-2 w-2 rounded-full ${monitorRunning ? 'bg-emerald-400' : 'bg-rose-400'}`}
              />
              {monitorLoading ? 'Checking…' : (monitorRunning ? 'Monitor running' : 'Monitor stopped')}
            </div>
            <h1 className="text-3xl font-black tracking-tight sm:text-4xl">Impact Plan</h1>
            <p className="max-w-2xl text-white/70">
              Monitor and track data usage for Impact Plan service lines. Edit customer mappings and export reports.
            </p>
            <div className="flex flex-wrap items-center gap-3 text-sm text-white/70">
              <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <i className="fas fa-satellite text-white/60"></i>
                {displayedForms.length} service lines
              </span>
              <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <i className="fas fa-filter text-white/60"></i>
                Filter: {filterOption}
              </span>
              <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <i className="fas fa-clock text-white/60"></i>
                Updated: {new Date().toLocaleTimeString()}
                </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate('/impact-plan/details')}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white/10 px-5 py-3 font-semibold text-white shadow-lg transition-all hover:bg-white/15"
            >
              <i className="fas fa-eye"></i>
              View details
            </button>
            <button
              onClick={user?.isSuperAdmin && !user?.hasAuditAccess ? (monitorRunning ? handleStopMonitor : handleStartMonitor) : undefined}
              disabled={monitorLoading || !user?.isSuperAdmin || user?.hasAuditAccess}
              className={`inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 font-semibold shadow-lg transition-all ${
                user?.isSuperAdmin && !user?.hasAuditAccess
                  ? (monitorRunning ? 'bg-rose-500 hover:bg-rose-600 text-white' : 'bg-white text-slate-900 hover:bg-slate-100')
                  : 'cursor-not-allowed bg-white/10 text-white/60'
              }`}
              title={!user?.isSuperAdmin ? 'Only Super Admin can control the monitor' : ''}
            >
              <i className={`fas ${monitorRunning ? 'fa-stop' : 'fa-play'}`}></i>
              {monitorRunning ? 'Stop monitor' : 'Start monitor'}
            </button>
          </div>
        </div>
      </div>

      {/* Impact Plan Section */}
      <div className="space-y-8">
        {/* Search and Controls */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Search Input */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                className="w-full rounded-2xl bg-gray-50 px-5 py-3 pl-12 text-gray-900 placeholder-gray-400 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                placeholder="Search customers, service lines, kit numbers..."
                value={formsSearch}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormsSearch(val);
                  if (debounce.current) clearTimeout(debounce.current);
                  debounce.current = setTimeout(() => setFormsSearch(val), 500);
                }}
              />
              <i className="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Filter Dropdown */}
              <select
                value={filterOption}
                onChange={(e) => setFilterOption(e.target.value)}
                className="rounded-2xl bg-gray-50 px-4 py-3 font-semibold text-gray-900 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500/30"
              >
                <option value="all">All Records</option>
                <option value="75">≥ 75% Usage</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
              
              {/* Export Button */}
              <button
                onClick={handleExport}
                className="bg-gradient-to-r from-emerald-600 to-green-600 text-white px-6 py-3 rounded-2xl hover:brightness-110 transition-all duration-200 font-semibold shadow-lg"
              >
                <i className="fas fa-file-excel mr-2"></i>
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Enhanced Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
          <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-gray-900">{formsData?.length || 0}</p>
                <p className="text-gray-600 font-medium text-sm">Total Users</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-lg">
                <i className="fas fa-file-alt text-lg"></i>
              </div>
            </div>
            <div className="mt-3">
              <span className="text-gray-500 text-xs">Service records</span>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {displayedForms.filter(f => f.isActive).length}
                </p>
                <p className="text-gray-600 font-medium text-sm">Active</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg">
                <i className="fas fa-check-circle text-lg"></i>
              </div>
            </div>
            <div className="mt-3 flex items-center">
              <div className="flex items-center text-green-600">
                <i className="fas fa-arrow-up text-xs mr-1"></i>
                <span className="text-xs font-medium">+5%</span>
              </div>
              <span className="text-gray-500 text-xs ml-2">this week</span>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {displayedForms.filter(f => !f.isActive).length}
                </p>
                <p className="text-gray-600 font-medium text-sm">Inactive</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-gray-500 to-gray-600 text-white shadow-lg">
                <i className="fas fa-pause-circle text-lg"></i>
              </div>
            </div>
            <div className="mt-3">
              <span className="text-gray-500 text-xs">Suspended services</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {(() => {
                    const now = new Date();
                    const currentYear = now.getFullYear();
                    const currentMonth = now.getMonth();
                    const optedOutCount = displayedForms.filter(f => {
                      // Check if status is "Opted Out"
                      if (f.status?.toLowerCase() === 'opted out') return true;
                      
                      // Inactive service lines have end_date (they've been cut off)
                      // Active service lines don't have end_date
                      // Count as opted out if end_date exists and is after the 5th day of current month
                      if (f.endDate) {
                        try {
                          const endDate = new Date(f.endDate);
                          // Check if date is valid
                          if (isNaN(endDate.getTime())) return false;
                          
                          // Extract year and month from endDate
                          const endYear = endDate.getFullYear();
                          const endMonth = endDate.getMonth();
                          
                          // Check if end_date is in the current month and after the 5th day
                          // OR if end_date is in a future month
                          if (endYear === currentYear && endMonth === currentMonth) {
                            // Same month: check if day is after 5th
                            const endDay = endDate.getDate();
                            return endDay > 5;
                          } else if (endYear > currentYear || (endYear === currentYear && endMonth > currentMonth)) {
                            // Future month: always opted out
                            return true;
                          }
                          
                          return false;
                        } catch (e) {
                          return false;
                        }
                      }
                      return false;
                    }).length;
                    
                    return optedOutCount;
                  })()}
                </p>
                <p className="text-gray-600 font-medium text-sm">Opted Out</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-red-500 to-red-600 text-white shadow-lg">
                <i className="fas fa-user-times text-lg"></i>
              </div>
            </div>
            <div className="mt-3">
              <span className="text-gray-500 text-xs">Monitoring disabled</span>
            </div>
          </div>

          <div className="md:col-span-2 bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 relative">
            <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-lg absolute top-4 right-4">
              <i className="fas fa-chart-pie text-xl"></i>
            </div>
            <div className="pr-16">
              <div className="flex items-baseline space-x-2">
                <p className="text-4xl font-bold text-gray-900 whitespace-nowrap">
                  {displayedForms
                    .reduce((acc, f) => acc + (Number(f.currentUsage) || 0), 0)
                    .toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                </p>
                <span className="text-xl font-bold text-gray-900">GB</span>
              </div>
              <p className="text-gray-600 font-medium mt-2">Total Usage</p>
              <p className="text-gray-500 text-sm mt-1">Data consumed</p>
            </div>
          </div>
        </div>

        {/* Modern Forms Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gray-50 px-6 py-5 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h3 className="text-xl font-black text-gray-900">Impact Plan</h3>
                {displayedForms.length > 0 && (
                  <span className="rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
                    {displayedForms.length} records
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-500">Last updated: {new Date().toLocaleTimeString()}</div>
            </div>
          </div>
          
          <table className="w-full forms-table" style={{ tableLayout: 'fixed', width: '100%' }}>
            <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider whitespace-nowrap" style={{ width: '25%' }}>
                    Customer Info
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider whitespace-nowrap" style={{ width: '25%' }}>
                    Service Details
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider whitespace-nowrap" style={{ width: '22%' }}>
                    Package & Usage
                  </th>
                  <th className="px-3 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider whitespace-nowrap" style={{ width: '8%' }}>
                    Status
                  </th>
                  <th className="px-3 py-4 text-center text-xs font-bold text-gray-600 uppercase tracking-wider whitespace-nowrap" style={{ width: '10%' }}>
                    Monitor
                  </th>
                  <th className="px-3 py-4 text-center text-xs font-bold text-gray-600 uppercase tracking-wider whitespace-nowrap" style={{ width: '10%' }}>
                    Email Status
                  </th>
                  <th className="px-3 py-4 text-right text-xs font-bold text-gray-600 uppercase tracking-wider whitespace-nowrap" style={{ width: '8%' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {displayedForms.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <i className="fas fa-file-alt text-4xl mb-4 text-gray-300"></i>
                        <p className="text-lg font-medium">No forms found</p>
                        <p className="text-sm mt-2 text-gray-600">
                          {formsSearch ? 'No forms match your search criteria. Try adjusting your search terms.' : 'No Impact Plan forms available.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  displayedForms.map((form, index) => (
                    <tr key={form.serviceLine} className={`border-b border-gray-100 hover:bg-gradient-to-r hover:from-purple-50 hover:to-indigo-50 transition-all duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                      <td className="px-4 py-6 whitespace-nowrap" style={{ width: '25%' }}>
                        <div>
                          {form.actualCustomerName && (
                            <div className="text-sm font-bold text-gray-900 mb-1">
                              {form.actualCustomerName}
                            </div>
                          )}
                          {form.nickname && (
                            <div className="text-sm font-semibold text-gray-700 mb-1">
                              {form.nickname}
                            </div>
                          )}
                          {form.activationDate && (
                            <div className="text-xs text-gray-500 mt-2 flex items-center">
                              <i className="fas fa-calendar mr-1"></i>
                              Activated: {new Date(form.activationDate).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-6 whitespace-nowrap" style={{ width: '25%' }}>
                        <div>
                          <div className="text-sm font-semibold text-gray-900 flex items-center">
                            <i className="fas fa-satellite mr-2 text-purple-500 flex-shrink-0"></i>
                            <span className="whitespace-nowrap">{form.serviceLine}</span>
                          </div>
                          {form.kitNumber && (
                            <div className="text-sm text-gray-500 flex items-center mt-1">
                              <i className="fas fa-box mr-2 text-gray-400 flex-shrink-0"></i>
                              <span className="whitespace-nowrap">Kit: {form.kitNumber}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-6 whitespace-nowrap" style={{ width: '22%' }}>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                            {Number(form.packageLimit) || 0} GB Package
                            {(form.dataTopup && Number(form.dataTopup) > 0) ? (
                              <span className="text-green-600"> + {Number(form.dataTopup)} GB Topup</span>
                            ) : ''}
                          </div>
                          <div className="mt-2">
                            <div className={`text-sm font-bold ${getUsageColor(Number(form.currentUsage) || 0, (Number(form.packageLimit) || 0) + (Number(form.dataTopup) || 0))}`}>
                              {Number(form.currentUsage) || 0} GB used
                            </div>
                            <div className="flex items-center mt-1">
                              <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                                {(() => {
                                  const usage = Number(form.currentUsage) || 0;
                                  const limit = Number(form.packageLimit) || 0;
                                  const topup = Number(form.dataTopup) || 0;
                                  const total = limit + topup;
                                  const percentage = total > 0 ? (usage / total) * 100 : 0;
                                  return (
                                    <div 
                                      className={`h-2 rounded-full ${
                                        percentage >= 90 ? 'bg-red-500' :
                                        percentage >= 75 ? 'bg-yellow-500' :
                                        percentage >= 50 ? 'bg-orange-500' : 'bg-green-500'
                                      }`}
                                      style={{ width: `${Math.min(percentage, 100)}%` }}
                                    />
                                  );
                                })()}
                              </div>
                              <span className="text-xs text-gray-600">
                                {(() => {
                                  const usage = Number(form.currentUsage) || 0;
                                  const limit = Number(form.packageLimit) || 0;
                                  const topup = Number(form.dataTopup) || 0;
                                  const percentage = form.usagePercentage !== undefined 
                                    ? Number(form.usagePercentage) 
                                    : (limit + topup > 0 ? (usage / (limit + topup)) * 100 : 0);
                                  return isNaN(percentage) ? '0.0' : percentage.toFixed(1);
                                })()}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-6 whitespace-nowrap" style={{ width: '8%' }}>
                        <span className={`inline-flex px-2 py-1 text-xs font-bold rounded-full ${form.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {form.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-3 py-6 whitespace-nowrap text-center" style={{ width: '10%' }}>
                        <div className="flex flex-col items-center justify-center space-y-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-lg ${getMonitorColor(form.monitorStatus)}`}>
                            {form.monitorStatus ?? 'Skipped'}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-6 whitespace-nowrap text-center" style={{ width: '10%' }}>
                        <div className="flex items-center justify-center space-x-2">
                          <div 
                            className={`w-3 h-3 rounded-full ${form.notified75 ? 'bg-green-500' : 'bg-red-500'}`}
                            title={form.notified75 ? '75% email sent' : '75% email not sent'}
                          ></div>
                          <div 
                            className={`w-3 h-3 rounded-full ${form.notified100 ? 'bg-green-500' : 'bg-red-500'}`}
                            title={form.notified100 ? '100% email sent' : '100% email not sent'}
                          ></div>
                        </div>
                      </td>
                      <td className="px-3 py-6 text-right whitespace-nowrap" style={{ width: '8%' }}>
                        {!user?.hasViewOnlyAccess && !user?.hasAuditAccess && (
                          <button
                            onClick={() => handleEdit(form)}
                            className="text-purple-600 hover:text-purple-800 p-3 hover:bg-purple-50 rounded-xl transition-all duration-200 group"
                            title="Edit Customer Information"
                          >
                            <i className="fas fa-edit text-lg group-hover:scale-110 transition-transform"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
        </div>
      </div>

      {/* Edit Customer Modal */}
      {showEditModal && editingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full">
            <div className="p-8 relative">
              {/* Close button */}
              <button
                onClick={handleCloseModal}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="fas fa-times text-xl"></i>
              </button>

              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Edit Customer Information
                </h3>
                <p className="text-gray-600">
                  Update customer name for this service line
                </p>
              </div>

              <form onSubmit={handleSaveCustomer} className="space-y-6">
                {/* Service Line (Read-only) */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">
                    Service Line Number
                  </label>
                  <input
                    type="text"
                    value={editingForm.serviceLine}
                    disabled
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl bg-gray-50 text-gray-600 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">This field cannot be edited</p>
                </div>

                {/* Customer Name */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">
                    Customer Name *
                  </label>
                  <input
                    type="text"
                    value={customerFormData.customerName}
                    onChange={(e) => setCustomerFormData({ ...customerFormData, customerName: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-100 focus:border-purple-500 transition-all duration-200"
                    required
                    placeholder="Enter customer name"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-8 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saveCustomerMutation.isLoading}
                    className="px-10 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saveCustomerMutation.isLoading ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save mr-2"></i>
                        Save
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImpactPlan;

